import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import Badge from '@repo/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getEndOfJourneyColumnCodes } from '../../../api/end-of-journey-templates/get-end-of-journey-column-codes'
import { EndOfJourneyColumnEnum } from '../../../lib/types/enums/end-of-stay-template/end-of-journey-column.enum'
import {
  RatingScaleEnum,
  ratingScaleEnumMapping
} from '../../../lib/types/enums/end-of-stay-template/rating-scale.enum'
import { EndOfJourneyQuestionInput } from '../../../lib/types/models/end-of-journey-template/end-of-journey-question-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import { ControlledListRadioGroup } from '../../elements/inputs/enum-radio-group'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<EndOfJourneyQuestionInput, true> & {
  className?: string
  templateId: number
}

export const END_OF_JOURNEY_QUESTION_SCHEMA: ZodType<EndOfJourneyQuestionInput> = z.object({
  question: createStringSchema('Question'),
  columnCode: createEnumSchema({ enumType: EndOfJourneyColumnEnum, fieldName: 'Colonne' }),
  ratingScale: createEnumSchema({ enumType: RatingScaleEnum, fieldName: 'Échelle d’évaluation' })
})

const EndOfJourneyQuestionForm = ({
  defaultValues,
  className,
  templateId,
  ...validationButtonProps
}: Props) => {
  const form = useForm<EndOfJourneyQuestionInput>({
    resolver: zodResolver(END_OF_JOURNEY_QUESTION_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: columnCodes } = useQuery({
    queryKey: ['end-of-journey-column-codes'],
    queryFn: getEndOfJourneyColumnCodes,
    staleTime: Infinity
  })
  const columnOptions = (columnCodes ?? []).map((column) => ({
    label: `${column.shortLabel} - ${column.label}`,
    value: column.code
  }))

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'divide-y divide-white-active'}>
          <div className={cn('flex flex-col gap-3', className)}>
            <Badge
              text={`Template #${templateId}`}
              variant="primaryLight"
              className="rounded-sm"
            />{' '}
            <FormField
              control={form.control}
              name="columnCode"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput<EndOfJourneyColumnEnum>
                      {...field}
                      required
                      searchable
                      label="Colonne"
                      options={columnOptions}
                      selectedOption={columnOptions.find((option) => option.value === field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      required
                      type="textarea"
                      label="Question"
                      rows={5}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <ControlledListRadioGroup<EndOfJourneyQuestionInput, RatingScaleEnum>
              required
              name="ratingScale"
              viewMode="column"
              wrapperClassName="gap-2"
              radioClassName="gap-2"
              label={'Échelle d’évaluation'}
              list={Object.values(RatingScaleEnum)}
              mapping={ratingScaleEnumMapping}
            />
          </div>
          <div className="pb-6 px-4">
            <ValidationButtons {...validationButtonProps} />
          </div>
        </div>
      </form>
    </Form>
  )
}

export default EndOfJourneyQuestionForm
